import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { LogIn } from 'lucide-react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { debtorLogin, getIdCountries, type IdCountry } from '../services/api';
import { useAuthStore } from '../store/authStore';

const EASE = [0.2, 0, 0, 1] as const;

const formContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const formChild: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: EASE },
  },
};

/**
 * Debtor self-service login. Auth pair is `case_number + CPR`:
 * Lexta hands the debtor the case number out-of-band (operator
 * handover or, later, an email), and the debtor types it together
 * with their own CPR. The backend hashes the CPR, looks up the
 * matching register entry, and issues a debtor JWT scoped to that
 * subject_key.
 *
 * Failure modes are deliberately collapsed into a single
 * "case_number + CPR don't match" message — same enumeration-safety
 * rule the operator/lender login uses.
 *
 * After successful login: navigate to /min-sag (the existing
 * debtor-cases route) — same destination LoginPage's `computeHome`
 * resolves for `role === 'debtor'`.
 */
export default function DebtorLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);

  const [caseNumber, setCaseNumber] = useState('');
  const [cpr, setCpr] = useState('');
  const [idCountry, setIdCountry] = useState<IdCountry>('DK');
  const idCountriesQuery = useQuery({
    queryKey: ['id-countries'],
    queryFn: getIdCountries,
    staleTime: 5 * 60_000,
  });
  const enabledIdCountries: IdCountry[] = idCountriesQuery.data?.enabled ?? ['DK'];
  const [caseFocused, setCaseFocused] = useState(false);
  const [cprFocused, setCprFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already-logged-in guard — same idea as LoginPage. If the
  // current session is a debtor, drop them on /min-sag; if it's
  // anything else, go to the operator/lender home so the page
  // doesn't loop.
  if (token && user) {
    if (user.role === 'debtor') {
      return <Navigate to="/min-sag" replace />;
    }
    return <Navigate to="/entries" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!caseNumber.trim() || !cpr.trim()) {
      setError(t('auth.requiredFields'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await debtorLogin(caseNumber.trim(), cpr.trim(), idCountry);
      setSession(response);
      navigate('/min-sag', { replace: true });
    } catch (err) {
      // The backend collapses every failure into 401
      // ``invalid_credentials``. Surface a single friendly message.
      const status = axios.isAxiosError(err) ? err.response?.status : null;
      if (status === 422) {
        // Wildly malformed input — Pydantic kicked it back. Hint
        // at the shape rather than the generic credentials line.
        setError(t('auth.debtorLoginShapeError'));
      } else {
        setError(t('auth.debtorLoginInvalid'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[440px] mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: EASE }}
        className="space-y-3 mb-8"
      >
        <div className="section-label">{t('auth.debtorLoginSectionLabel')}</div>
        <h1 className="font-mono text-2xl tracking-tight text-[var(--color-ink)]">
          {t('auth.debtorLoginTitle')}
        </h1>
        <p className="text-sm text-[var(--color-ink-soft)] leading-relaxed">
          {t('auth.debtorLoginLede')}
        </p>
      </motion.div>

      <div className="draw-rule h-px mb-8" aria-hidden />

      <motion.form
        variants={formContainer}
        initial="hidden"
        animate="visible"
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <motion.div variants={formChild}>
          <label
            htmlFor="case-number"
            className="section-label block mb-2"
          >
            {t('auth.debtorLoginCaseNumberLabel')}
          </label>
          <div className="relative">
            <input
              id="case-number"
              type="text"
              autoComplete="off"
              value={caseNumber}
              onChange={(e) => setCaseNumber(e.target.value)}
              onFocus={() => setCaseFocused(true)}
              onBlur={() => setCaseFocused(false)}
              className="w-full font-mono"
              required
              placeholder={t('auth.debtorLoginCaseNumberPlaceholder')}
              spellCheck={false}
            />
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-px bg-[var(--color-ink)]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: caseFocused ? 1 : 0 }}
              transition={{ duration: 0.22, ease: EASE }}
              style={{ transformOrigin: '0% 50%' }}
            />
          </div>
          <span className="block mt-1 text-[11px] font-mono text-[var(--color-ink-muted)]">
            {t('auth.debtorLoginCaseNumberHint')}
          </span>
        </motion.div>

        {enabledIdCountries.length > 1 && (
          <motion.div variants={formChild}>
            <label htmlFor="id-country" className="section-label block mb-2">
              {t('auth.debtorLoginIdCountryLabel')}
            </label>
            <select
              id="id-country"
              value={idCountry}
              onChange={(e) => {
                setIdCountry(e.target.value as IdCountry);
                setCpr('');
              }}
              className="w-full font-mono"
            >
              {enabledIdCountries.map((code) => (
                <option key={code} value={code}>
                  {t(`idCountry.${code}`)}
                </option>
              ))}
            </select>
          </motion.div>
        )}

        <motion.div variants={formChild}>
          <label htmlFor="cpr" className="section-label block mb-2">
            {t(
              idCountry === 'DK'
                ? 'auth.debtorLoginCprLabel'
                : `auth.debtorLoginIdLabel_${idCountry}`,
            )}
          </label>
          <div className="relative">
            <input
              id="cpr"
              type="text"
              autoComplete="off"
              value={cpr}
              onChange={(e) => setCpr(e.target.value)}
              onFocus={() => setCprFocused(true)}
              onBlur={() => setCprFocused(false)}
              className="w-full font-mono"
              required
              placeholder={t(`auth.debtorLoginIdPlaceholder_${idCountry}`)}
              spellCheck={false}
              inputMode="numeric"
            />
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-px bg-[var(--color-ink)]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: cprFocused ? 1 : 0 }}
              transition={{ duration: 0.22, ease: EASE }}
              style={{ transformOrigin: '0% 50%' }}
            />
          </div>
        </motion.div>

        <motion.div variants={formChild} className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <motion.button
            type="submit"
            disabled={submitting}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            animate={
              submitting ? { opacity: [1, 0.7, 1] } : { opacity: 1 }
            }
            transition={
              submitting
                ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.15 }
            }
          >
            <LogIn className="w-4 h-4" strokeWidth={1.5} />
            {submitting
              ? t('auth.signingIn')
              : t('auth.debtorLoginSubmit')}
          </motion.button>
          <Link
            to="/login"
            className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-primary)] hover:text-[var(--color-primary-strong)]"
          >
            {t('auth.debtorLoginOperatorAffordance')}
          </Link>
        </motion.div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key={error}
              role="alert"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.18, ease: EASE }}
              className="text-xs font-mono text-[var(--color-alert)]"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.form>
    </div>
  );
}
